import React, { useState, useEffect } from 'react';
import { TreatmentType, CreateTreatmentTypeData, CATEGORIES_SOIN, CategorieSoin } from '../../types';

interface TreatmentTypeFormProps {
  treatmentType?: TreatmentType | null;
  onSubmit: (data: CreateTreatmentTypeData) => void;
  onCancel: () => void;
  loading: boolean;
}

const TreatmentTypeForm: React.FC<TreatmentTypeFormProps> = ({ treatmentType, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState<CreateTreatmentTypeData>({
    name: '',
    description: '',
    base_price: 0,
    duration_minutes: 30,
    categorie: '',
    description_patient: '',
    prix_max: null,
    visible_whatsapp: true,
    non_facturable: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (treatmentType) {
      setFormData({
        name: treatmentType.name,
        description: treatmentType.description || '',
        base_price: treatmentType.base_price,
        duration_minutes: treatmentType.duration_minutes,
        categorie: treatmentType.categorie ?? '',
        description_patient: treatmentType.description_patient ?? '',
        prix_max: treatmentType.prix_max ?? null,
        visible_whatsapp: treatmentType.visible_whatsapp ?? true,
        non_facturable: treatmentType.non_facturable ?? false,
      });
    }
  }, [treatmentType]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }

    if (!formData.non_facturable && formData.base_price <= 0) {
      newErrors.base_price = 'Le prix doit être supérieur à 0';
    }

    if (formData.duration_minutes <= 0) {
      newErrors.duration_minutes = 'La durée doit être supérieure à 0';
    }

    if (formData.prix_max != null && formData.prix_max <= formData.base_price) {
      newErrors.prix_max = 'Le prix maximum doit dépasser le prix de base (sinon, laissez vide)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto mb-20 p-5 border w-96 max-w-[calc(100%-2rem)] shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {treatmentType ? 'Modifier le type de traitement' : 'Nouveau type de traitement'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Nom *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm text-gray-900 ${
                  errors.name ? 'border-red-300' : ''
                }`}
                disabled={loading}
                placeholder="Ex: Consultation dentaire"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                rows={3}
                value={formData.description}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm text-gray-900"
                disabled={loading}
                placeholder="Description détaillée du traitement..."
              />
            </div>

            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={formData.non_facturable ?? false}
                onChange={e => {
                  const nonFacturable = e.target.checked;
                  setFormData(prev => ({
                    ...prev,
                    non_facturable: nonFacturable,
                    ...(nonFacturable && { base_price: 0, prix_max: null }),
                  }));
                  setErrors(prev => ({ ...prev, base_price: '', prix_max: '' }));
                }}
                className="mt-0.5 rounded text-teal-600"
                disabled={loading}
              />
              <span>
                Non facturable (suite de soins)
                <span className="block text-xs text-gray-500">
                  Séance d'un traitement déjà payé : prix 0, jamais facturé. Une consultation qui
                  n'en compte que se clôture sans facture. Jamais proposé sur WhatsApp.
                </span>
              </span>
            </label>

            <div>
              <label htmlFor="base_price" className="block text-sm font-medium text-gray-700">
                Prix de base (GNF) *
              </label>
              <input
                type="number"
                id="base_price"
                name="base_price"
                min="0"
                step="100"
                value={formData.base_price}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm text-gray-900 ${
                  errors.base_price ? 'border-red-300' : ''
                }`}
                disabled={loading || formData.non_facturable}
              />
              {errors.base_price && (
                <p className="mt-1 text-sm text-red-600">{errors.base_price}</p>
              )}
            </div>

            <div>
              <label htmlFor="duration_minutes" className="block text-sm font-medium text-gray-700">
                Durée (minutes) *
              </label>
              <input
                type="number"
                id="duration_minutes"
                name="duration_minutes"
                min="1"
                value={formData.duration_minutes}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm text-gray-900 ${
                  errors.duration_minutes ? 'border-red-300' : ''
                }`}
                disabled={loading}
              />
              {errors.duration_minutes && (
                <p className="mt-1 text-sm text-red-600">{errors.duration_minutes}</p>
              )}
            </div>

            <fieldset className="border-t border-gray-100 pt-4 space-y-4">
              <legend className="text-sm font-semibold text-gray-700">Catalogue WhatsApp</legend>

              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={formData.visible_whatsapp ?? true}
                  onChange={e => setFormData(prev => ({ ...prev, visible_whatsapp: e.target.checked }))}
                  className="rounded text-teal-600"
                  disabled={loading}
                />
                Proposé aux patients sur WhatsApp
              </label>

              <div>
                <label htmlFor="categorie" className="block text-sm font-medium text-gray-700">Catégorie</label>
                <select
                  id="categorie"
                  value={formData.categorie ?? ''}
                  onChange={e => setFormData(prev => ({ ...prev, categorie: e.target.value as CategorieSoin }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm text-gray-700"
                  disabled={loading}
                >
                  {CATEGORIES_SOIN.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>

              <div>
                <label htmlFor="description_patient" className="block text-sm font-medium text-gray-700">
                  Description pour le patient
                </label>
                <textarea
                  id="description_patient"
                  name="description_patient"
                  rows={2}
                  maxLength={300}
                  value={formData.description_patient ?? ''}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm text-gray-700"
                  disabled={loading}
                  placeholder="Ex : Nettoyage du tartre pour des gencives saines."
                />
              </div>

              <div>
                <label htmlFor="prix_max" className="block text-sm font-medium text-gray-700">
                  Prix maximum (GNF) — si le prix dépend du diagnostic
                </label>
                <input
                  type="number"
                  id="prix_max"
                  min="0"
                  step="100"
                  value={formData.prix_max ?? ''}
                  onChange={e => setFormData(prev => ({ ...prev, prix_max: e.target.value === '' ? null : Number(e.target.value) }))}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm text-gray-700 ${
                    errors.prix_max ? 'border-red-300' : ''
                  }`}
                  disabled={loading || formData.non_facturable}
                  placeholder="Vide : prix fixe"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Renseigné, le bot annonce « à partir de » le prix de base, avec la mention « devis précis après consultation ».
                </p>
                {errors.prix_max && <p className="mt-1 text-sm text-red-600">{errors.prix_max}</p>}
              </div>
            </fieldset>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                disabled={loading}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 border border-transparent rounded-md shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Enregistrement...' : treatmentType ? 'Modifier' : 'Créer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TreatmentTypeForm;